import React, { useState, useEffect } from 'react';
import { Utils, authJsonHeaders, formatDate, handleSessionExpired } from '../../utils';
import { MdSearch, MdVisibility } from 'react-icons/md';
import Modal from '../common/Modal';
import ListPaginationBar from '../common/ListPaginationBar';

const AdminAppUsers = () => {
    const [appUsers, setAppUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        fetchAppUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, search]);

    const fetchAppUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/app-users', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ page: pagination.page, limit: pagination.limit, search })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                setAppUsers(result.data);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error('Error fetching app users:', error);
        }
        setLoading(false);
    };

    const parseTags = (tags) => {
        if (!tags) return {};
        try {
            return typeof tags === 'string' ? JSON.parse(tags) : tags;
        } catch { return {}; }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>App Users</h1>
                    <p style={{ color: '#6b7280', marginTop: '8px' }}>End users of all applications</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'white', padding: '8px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <MdSearch size={20} color="#9ca3af" />
                    <input
                        type="text"
                        placeholder="Search by ID, phone, email..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                        style={{ border: 'none', outline: 'none', fontSize: '14px', width: '220px' }}
                    />
                </div>
            </div>

            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f9fafb' }}>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>notifySphere ID</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Contact</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Subscriptions</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Tags</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>First Session</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Last Session</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
                        ) : appUsers.length === 0 ? (
                            <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No app users found</td></tr>
                        ) : appUsers.map(user => {
                            const tags = parseTags(user.tags);
                            return (
                                <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#4f46e5' }}>{user.notify_sphere_id}</div>
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ color: '#374151' }}>{user.email || user.phone || 'N/A'}</div>
                                        {user.userName && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{user.userName}</div>}
                                    </td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}>
                                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', background: '#dbeafe', color: '#1d4ed8' }}>
                                            {user.subscriptions?.length || 0}
                                        </span>
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                            {Object.entries(tags).slice(0, 3).map(([key, value], idx) => (
                                                <span key={idx} style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: '#f3f4f6', color: '#6b7280' }}>
                                                    {key}: {value}
                                                </span>
                                            ))}
                                            {Object.keys(tags).length > 3 && (
                                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>+{Object.keys(tags).length - 3} more</span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                                        {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                                    </td>
                                    <td style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                                        {user.updatedAt ? formatDate(user.updatedAt) : 'N/A'}
                                    </td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}>
                                        <button onClick={() => setSelectedUser(user)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5' }}>
                                            <MdVisibility size={20} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <ListPaginationBar
                page={pagination.page}
                setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
                total={pagination.total}
                pageSize={pagination.limit}
                totalPages={pagination.totalPages}
                ariaLabel="Admin app users pagination"
                className="px-3 py-3"
                style={{ marginTop: '16px' }}
                loading={loading}
            />

            {selectedUser && (
                <Modal
                    title="App User Details"
                    onClose={() => setSelectedUser(null)}
                    maxWidth="700px"
                    footer={
                        <button type="button" onClick={() => setSelectedUser(null)} style={{ padding: '10px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                            Close
                        </button>
                    }
                >
                        <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
                            <div><strong>notifySphere ID:</strong> <code>{selectedUser.notify_sphere_id}</code></div>
                            <div><strong>Email:</strong> {selectedUser.email || 'N/A'}</div>
                            <div><strong>Phone:</strong> {selectedUser.phone || 'N/A'}</div>
                            <div><strong>Location:</strong> {selectedUser.lat && selectedUser.long ? `${selectedUser.lat}, ${selectedUser.long}` : 'N/A'}</div>
                            <div><strong>City/State:</strong> {[selectedUser.city, selectedUser.state].filter(Boolean).join(', ') || 'N/A'}</div>
                        </div>
                        <h3>Tags</h3>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
                            {Object.entries(parseTags(selectedUser.tags)).map(([key, value], idx) => (
                                <span key={idx} style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '13px', background: '#e0e7ff', color: '#4338ca' }}>
                                    <strong>{key}:</strong> {value}
                                </span>
                            ))}
                        </div>
                        <h3>Subscriptions ({selectedUser.subscriptions?.length || 0})</h3>
                        <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                            {selectedUser.subscriptions?.map((sub, idx) => (
                                <div key={idx} style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px', fontSize: '13px' }}>
                                    <div><strong>ID:</strong> {sub.subscription_id}</div>
                                    <div><strong>Type:</strong> {sub.type || 'N/A'} | <strong>Device:</strong> {sub.device_model || 'N/A'}</div>
                                </div>
                            ))}
                        </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminAppUsers;
